
import { useTranslation } from 'react-i18next';

export default function RoomsSection() {
  const { t } = useTranslation();

  const experiences = [
    {
      id: 1,
      title: 'Rotary Phone',
      titleJa: '黒電話',
      description: 'Experience the nostalgic sound of a real 1970s rotary dial phone',
      descriptionJa: 'ダイヤルを回す懐かしい音',
      image: 'https://public.readdy.ai/ai/img_res/42c10a2b724d527b7269c10845df40e4.jpg',
    },
    {
      id: 2,
      title: 'Chabudai Table',
      titleJa: 'ちゃぶ台',
      description: 'Traditional low dining table where families gathered in Showa era',
      descriptionJa: '家族が集まる昭和の食卓',
      image: 'https://readdy.ai/api/search-image?query=traditional%20Japanese%20chabudai%20low%20wooden%20table%20in%20tatami%20room%20with%20tea%20set%20and%20cushions%20warm%20ambient%20lighting%201970s%20Showa%20era%20home%20interior%20film%20photography%20style%20nostalgic%20atmosphere%20vintage%20aesthetic%20cozy%20setting&width=600&height=400&seq=showa-chabudai-001&orientation=landscape',
    },
    {
      id: 3,
      title: 'Retro Appliances',
      titleJa: '昭和家電',
      description: 'Working vintage appliances from the golden age of Japanese design',
      descriptionJa: '動く昭和の家電たち',
      image: 'https://readdy.ai/api/search-image?query=vintage%20Japanese%20home%20appliances%20from%201970s%20Showa%20era%20including%20retro%20fan%20radio%20and%20electric%20heater%20on%20wooden%20shelf%20warm%20lighting%20nostalgic%20atmosphere%20film%20grain%20texture%20authentic%20period%20details%20sepia%20tones&width=600&height=400&seq=showa-appliances-001&orientation=landscape',
    },
    {
      id: 4,
      title: 'Tatami & Shoji',
      titleJa: '畳と障子',
      description: 'Soft light filtering through paper screens onto traditional tatami mats',
      descriptionJa: '障子から差し込む柔らかな光',
      image: 'https://readdy.ai/api/search-image?query=traditional%20Japanese%20tatami%20room%20with%20shoji%20paper%20sliding%20doors%20soft%20natural%20light%20filtering%20through%20creating%20beautiful%20shadows%201970s%20Showa%20era%20interior%20peaceful%20atmosphere%20film%20photography%20aesthetic%20warm%20tones%20minimalist%20zen%20space&width=600&height=400&seq=showa-tatami-001&orientation=landscape',
    },
    {
      id: 5,
      title: 'Retro Posters',
      titleJa: 'レトロポスター',
      description: 'Original movie and travel posters from 1970s Japan',
      descriptionJa: '昭和の映画・旅行ポスター',
      image: 'https://readdy.ai/api/search-image?query=vintage%20Japanese%20movie%20and%20travel%20posters%20from%201970s%20Showa%20era%20hanging%20on%20wooden%20wall%20retro%20typography%20nostalgic%20colors%20film%20grain%20texture%20authentic%20period%20advertising%20art%20warm%20ambient%20lighting%20cultural%20artifacts&width=600&height=400&seq=showa-posters-001&orientation=landscape',
    },
    {
      id: 6,
      title: 'Wooden Beams',
      titleJa: '古民家の梁',
      description: 'Exposed wooden beams showing decades of history and craftsmanship',
      descriptionJa: '歴史を刻む木の梁',
      image: 'https://readdy.ai/api/search-image?query=traditional%20Japanese%20kominka%20house%20interior%20with%20exposed%20wooden%20ceiling%20beams%20showing%20aged%20wood%20texture%20and%20craftsmanship%20warm%20lighting%201970s%20Showa%20era%20architecture%20film%20photography%20style%20nostalgic%20atmosphere%20authentic%20historical%20details&width=600&height=400&seq=showa-beams-001&orientation=landscape',
    },
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-showa-cream via-amber-50 to-showa-cream relative overflow-hidden">
      {/* Film grain texture overlay */}
      <div className="absolute inset-0 opacity-30 pointer-events-none film-grain"></div>
      
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block mb-4">
            <span className="text-showa-rust font-serif text-sm tracking-[0.3em] uppercase border-b-2 border-showa-rust pb-2">
              Experience
            </span>
          </div>
          <h2 className="text-5xl font-serif text-showa-brown mb-6 tracking-tight">
            Showa Experience
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed font-light">
            Step into 1970s Japan. Every detail tells a story of the Showa era.
          </p>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mt-4 font-light">
            昭和の世界へようこそ。すべてのアイテムが物語を語ります。
          </p>
        </div>

        {/* Experience Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {experiences.map((exp) => (
            <div
              key={exp.id}
              className="group relative bg-white rounded-sm overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 border border-showa-brown/10"
            >
              {/* Image Container */}
              <div className="relative h-64 overflow-hidden">
                <img
                  src={exp.image}
                  alt={exp.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 sepia-filter"
                />
                {/* Film grain overlay on image */}
                <div className="absolute inset-0 film-grain opacity-40 pointer-events-none"></div>
                {/* Warm overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-showa-brown/60 via-transparent to-transparent opacity-80"></div>
              </div>

              {/* Content */}
              <div className="p-6 bg-gradient-to-b from-white to-showa-cream/30">
                <h3 className="text-2xl font-serif text-showa-brown mb-2 tracking-tight">
                  {exp.title}
                </h3>
                <p className="text-sm text-showa-rust font-medium mb-3 tracking-wide">
                  {exp.titleJa}
                </p>
                <p className="text-gray-700 leading-relaxed font-light mb-2">
                  {exp.description}
                </p>
                <p className="text-sm text-gray-600 font-light">
                  {exp.descriptionJa}
                </p>
              </div>

              {/* Decorative corner */}
              <div className="absolute top-4 right-4 w-12 h-12 border-t-2 border-r-2 border-showa-yamabuki opacity-60"></div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <div className="inline-block bg-gradient-to-r from-showa-rust to-showa-yamabuki text-white px-8 py-4 rounded-sm shadow-lg">
            <p className="text-lg font-serif tracking-wide">
              Like stepping into a Japanese film from the 1970s
            </p>
            <p className="text-sm mt-2 opacity-90">
              まるで昭和の映画の中に入ったような体験
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
